import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Switches the interface language.
 *
 * Two things have to move together. `POST /api/Language/change` writes the
 * ASP.NET culture cookie, which is what any remaining Razor page and every
 * server-localized payload (navigation titles, validation messages) reads;
 * `i18n.changeLanguage` swaps the client bundle. The Razor switcher followed the
 * POST with `window.location.reload()` — here the cached server responses are
 * invalidated instead, which refetches the localized ones without discarding the
 * page the user is on.
 */
export function useChangeLanguage() {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();

  return useCallback(
    async (culture: string) => {
      if (culture === i18n.language) return;

      try {
        await api.post('/api/Language/change', { culture });
      } catch {
        // The cookie is a server-side preference; failing to store it must not
        // stop the UI from switching, it only means the choice is not persisted.
      }

      await i18n.changeLanguage(culture);
      await queryClient.invalidateQueries();
    },
    [i18n, queryClient],
  );
}
